/* VSKLO checkout guard — country-aware checkout.
   Source of truth: Shopify Liquid storefront context, never locale or URL prefixes.
   - Ukraine (VSKLO_STOREFRONT.country === UA) -> Chekly with the current Shopify cart token.
   - Every other Shopify country -> native Shopify /checkout.
   - If storefront context is unavailable, fail safe to native Shopify checkout rather than guessing.
   - No cart clearing, cart rebuild, line reordering, property sanitizing or snapshot/restore logic.
   - Direct personalization backup is fire-and-forget and never delays checkout redirect.
*/
(function () {
  'use strict';

  if (window.__VSKLO_CHECKOUT_GUARD_LOADED__) return;
  window.__VSKLO_CHECKOUT_GUARD_LOADED__ = true;
  window.__VSKLO_CHECKOUT_OWNER__ = 'vsklo-checkout-guard-country-context';

  function activeStorefrontCountry() {
    var context = window.VSKLO_STOREFRONT;
    var country = context && typeof context.country === 'string' ? context.country : '';
    return String(country || '').trim().toUpperCase();
  }

  function useCheklyCheckout() {
    return activeStorefrontCountry() === 'UA';
  }

  var checkoutInProgress = false;
  var cartFetchInFlight = null;
  var cachedCart = null;
  var cachedCartAt = 0;

  var CART_FAST_WAIT_MS = 900;
  var CART_PENDING_WAIT_MS = 1900;
  var CART_CACHE_TTL_MS = 15000;

  function clean(value) {
    return String(value == null ? '' : value).trim();
  }

  function lower(value) {
    return clean(value).toLowerCase();
  }

  function t(scope, key, fallback, params) {
    var value = window.vskloStrings && window.vskloStrings[scope] && window.vskloStrings[scope][key];
    if (!value || (typeof value === 'string' && value.indexOf('Translation missing') !== -1)) value = fallback || '';
    if (params && typeof value === 'string') {
      Object.keys(params).forEach(function (name) {
        value = value.replace(new RegExp('\\{\\{\\s*' + name + '\\s*\\}\\}', 'g'), params[name]);
      });
    }
    if (typeof value === 'string' && /\{\{\s*[\w.]+\s*\}\}/.test(value)) return fallback || '';
    return value;
  }

  function showMessage(message) {
    var value = clean(message);
    if (!value) return;
    if (/\{\{[^}]+\}\}/.test(value) || /^(Cart |Shopify |VSKLO |Invalid |TypeError|Error:|Operation timed out)/i.test(value)) {
      value = t('cart', 'checkoutRedirectError', 'Перевірте кошик і спробуйте оформити замовлення ще раз.');
    }
    var old = document.querySelector('[data-vsklo-cart-message]');
    if (old) old.remove();

    var box = document.createElement('div');
    box.setAttribute('data-vsklo-cart-message', 'true');
    box.setAttribute('role', 'alert');
    box.className = 'vsklo-cart-message';
    box.textContent = value;

    // Show validation at the checkout form, not over the product cards.
    var host = document.querySelector('#main-cart-items .vsklo-cart-summary') ||
      document.querySelector('#minicart_wrapper .cart-footer');
    if (host) {
      box.setAttribute('data-vsklo-cart-message-inline', 'true');
      var checkout = host.querySelector('[data-vsklo-checkout], .vsklo-checkout-button, [name="checkout"], .btn-checkout');
      host.insertBefore(box, checkout || host.firstChild);
    } else {
      document.body.appendChild(box);
    }
    window.setTimeout(function () {
      if (box.parentNode) box.remove();
    }, 12000);
  }
  window.VSKLOShowUserMessage = window.VSKLOShowUserMessage || showMessage;

  function route(path) {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root.replace(/\/$/, '') + path;
  }

  function isCartPage() {
    var path = window.location && window.location.pathname ? window.location.pathname : '';
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    var cartPath = root.replace(/\/$/, '') + '/cart';
    return path === '/cart' || path === cartPath || /\/cart\/?$/.test(path);
  }

  function redirectToCartPage() {
    window.location.href = route('/cart');
  }

  function withTimeout(promise, ms, fallback) {
    var timer;
    return Promise.race([
      Promise.resolve(promise),
      new Promise(function (resolve) {
        timer = window.setTimeout(function () {
          resolve(fallback);
        }, ms);
      }),
    ]).finally(function () {
      window.clearTimeout(timer);
    });
  }

  function withTimeoutError(promise, ms, message) {
    var timer;
    return Promise.race([
      Promise.resolve(promise),
      new Promise(function (_, reject) {
        timer = window.setTimeout(function () {
          reject(new Error(message || 'Operation timed out'));
        }, ms);
      }),
    ]).finally(function () {
      window.clearTimeout(timer);
    });
  }

  function fetchCart() {
    return fetch(route('/cart.js'), {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    }).then(function (response) {
      if (!response.ok) throw new Error('Cart fetch failed');
      return response.json().then(function (cart) {
        if (cart && cart.token) window.__VSKLO_CART_TOKEN__ = cart.token;
        if (cart && Array.isArray(cart.items)) {
          cachedCart = cart;
          cachedCartAt = Date.now();
        }
        return cart;
      });
    });
  }

  function fetchCartFresh() {
    if (!cartFetchInFlight) {
      cartFetchInFlight = fetchCart().finally(function () {
        cartFetchInFlight = null;
      });
    }
    return cartFetchInFlight;
  }

  function postJson(url, body) {
    return fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(body || {}),
    }).then(function (response) {
      return response
        .json()
        .catch(function () {
          return {};
        })
        .then(function (json) {
          if (!response.ok || json.status || json.errors) {
            throw new Error(json.description || json.message || json.errors || 'Cart request failed');
          }
          return json;
        });
    });
  }

  function cartHasPendingChanges() {
    try {
      return typeof window.VSKLOCartHasPendingChanges === 'function' && !!window.VSKLOCartHasPendingChanges();
    } catch (error) {
      return false;
    }
  }

  async function waitForCartMutationQueue() {
    var queue = window.VSKLOCartMutationQueue;
    if (!queue || typeof queue.whenIdle !== 'function') return false;
    await withTimeoutError(
      queue.whenIdle(),
      5000,
      t('cart', 'checkoutSyncError', 'Не вдалося дочекатися збереження змін у кошику. Спробуйте ще раз.'),
    );
    return true;
  }

  async function runBeforeCheckoutHooks() {
    var hook = window.VSKLOCartBeforeCheckout;
    var hadPendingChanges = cartHasPendingChanges();
    var queue = window.VSKLOCartMutationQueue;
    var queueHadPending = !!(
      queue &&
      typeof queue.hasPending === 'function' &&
      queue.hasPending()
    );

    if (typeof hook === 'function' && hadPendingChanges) {
      await withTimeoutError(
        Promise.resolve(hook()),
        5000,
        t('cart', 'checkoutSyncError', 'Не вдалося підтвердити збереження змін у кошику. Спробуйте ще раз.'),
      );
    }

    // Always wait for the shared mutation queue. Gift packaging / add-on writes
    // can be in flight even when quantity/text dirty flags are already clear.
    await waitForCartMutationQueue();
    return hadPendingChanges || queueHadPending;
  }

  async function syncAttributionBeforeCheckout() {
    if (typeof window.VSKLO_UTM_SYNC_NOW !== 'function') return false;

    try {
      await withTimeout(Promise.resolve(window.VSKLO_UTM_SYNC_NOW({ reason: 'checkout-guard', force: true })), 1200, null);
      return true;
    } catch (error) {
      console.warn('VSKLO UTM pre-checkout sync skipped:', error);
      return false;
    }
  }

  function visibleCartNoteValue() {
    var field = document.querySelector('[data-vsklo-customer-comment], #Cart-note, textarea[name="note"]');
    if (!field) return null;
    return clean(field.value || '');
  }

  function checkoutCustomerComment() {
    var note = visibleCartNoteValue();
    if (!note) return '';
    return note.replace(/[;\r\n]+/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 500).trim();
  }

  // Chekly does not preserve Shopify cart notes.
  // UA Chekly checkout forwards only the sanitized visible note via dedicated utm_comment;
  // native Shopify checkout persists the note through /cart/update.js before redirect.
  async function syncAttributionBeforeNativeCheckout() {
    if (typeof window.VSKLO_UTM_SYNC_NOW !== 'function') return false;

    try {
      await withTimeout(Promise.resolve(window.VSKLO_UTM_SYNC_NOW({ reason: 'native-checkout', force: true })), 450, null);
      return true;
    } catch (error) {
      console.warn('VSKLO UTM pre-native-checkout sync skipped:', error);
      return false;
    }
  }

  async function syncCartNoteForNativeCheckout() {
    var note = visibleCartNoteValue();
    if (note === null) return;

    var task = function () { return postJson(route('/cart/update.js'), { note: note }); };
    var promise = window.VSKLOCartMutationQueue && typeof window.VSKLOCartMutationQueue.enqueue === 'function'
      ? window.VSKLOCartMutationQueue.enqueue('checkout-cart-note', task)
      : task();

    // The checkout redirect is intercepted by this guard, so the visible field
    // is not guaranteed to submit natively. Persist even an empty string and
    // block checkout if Shopify does not confirm the write.
    await withTimeoutError(
      promise,
      1600,
      t('cart', 'checkoutSyncError', 'Не вдалося зберегти коментар до замовлення.'),
    );
  }

  // Cleanup must finish before the UA Chekly or international checkout
  // can consume the cart. An old orphaned service is removed on the cart page
  // and the customer reviews the reloaded total before continuing.
  async function finishOrphanCleanupBeforeCheckout() {
    if (typeof window.VSKLOCartOrphanCleanup !== 'function') return;
    var result = await withTimeoutError(
      Promise.resolve(window.VSKLOCartOrphanCleanup()),
      10000,
      t('cart', 'checkoutSyncError', 'Не вдалося перевірити додаткові послуги. Оновіть кошик.')
    );
    if ((result && result.removed > 0) || window.__VSKLO_ORPHAN_CART_RELOAD_PENDING__) {
      throw new Error(t('cart', 'orphanRemoved',
        'Зайву послугу видалено. Перевірте оновлений кошик перед оформленням.'));
    }
  }

  async function prepareCartForNativeCheckout() {
    var hadPendingChanges = await runBeforeCheckoutHooks();
    await finishOrphanCleanupBeforeCheckout();

    // Never race two /cart/update.js writers. Add-on/quantity writes finish first,
    // then attribution, then the visible cart note.
    await syncAttributionBeforeNativeCheckout();
    await syncCartNoteForNativeCheckout();

    cachedCartAt = 0;
    var waitMs = hadPendingChanges ? CART_PENDING_WAIT_MS : CART_FAST_WAIT_MS;
    var cart = await getCartForCheckout(waitMs);

    if (!cart) {
      throw new Error(t('cart', 'checkoutRedirectError', 'Оформлення тимчасово недоступне. Оновіть сторінку і спробуйте ще раз.'));
    }

    return validateCartIntegrity(cart);
  }

  function redirectToNativeCheckout() {
    window.location.href = route('/checkout');
  }

  function propsOf(item) {
    return item && item.properties && typeof item.properties === 'object' ? item.properties : {};
  }

  function prop(item, key) {
    var props = propsOf(item);
    return Object.prototype.hasOwnProperty.call(props, key) ? clean(props[key]) : '';
  }

  function firstProp(item, keys) {
    for (var i = 0; i < keys.length; i += 1) {
      var value = prop(item, keys[i]);
      if (value) return value;
    }
    return '';
  }

  function titleLooksLikeService(item) {
    var title = lower(item && (item.product_title || item.title || item.handle || ''));
    if (!title) return false;
    return (
      title.indexOf('гравіювання') === 0 ||
      title.indexOf('graviiuvannia') === 0 ||
      title.indexOf('engraving') === 0 ||
      title.indexOf('нанесення') === 0 ||
      title.indexOf('personalization') === 0 ||
      title.indexOf('персоналізація') === 0 ||
      title.indexOf('подарунковий пакет') === 0 ||
      title.indexOf('подарункове пакування') === 0 ||
      title.indexOf('gift package') === 0 ||
      title.indexOf('gift packaging') === 0 ||
      title.indexOf('gift bag') === 0 ||
      title.indexOf('package engraving') === 0 ||
      title.indexOf('packaging engraving') === 0
    );
  }

  function isServiceItem(item) {
    if (!item) return false;
    var props = propsOf(item);
    var role = lower(props._vsklo_role || props['_vsklo_role']);
    var type = lower(item.product_type || item.type || '');
    var marker = firstProp(item, ['_vsklo_addon', '_vsklo_service_title', '_vsklo_service_type', 'Послуга']);
    var parent = firstProp(item, [
      '_vsklo_parent_variant_id',
      '_linked_variant_id',
      'VSKLO_PARENT_VARIANT_ID',
      '_vsklo_parent_title',
      'До товару',
    ]);

    if (role === 'parent' || role === 'main') return false;
    if (role === 'addon' || role === 'service') return true;
    if (marker || parent) return true;
    if (type === 'additional option' || type === 'addon' || type === 'add-on' || type === 'service' || type === 'services') return true;
    if (titleLooksLikeService(item)) return true;
    return false;
  }

  function cartItems(cart) {
    return cart && Array.isArray(cart.items) ? cart.items : [];
  }

  function cartHasPhysicalProduct(cart) {
    return cartItems(cart).some(function (item) {
      return !isServiceItem(item);
    });
  }

  function itemTitle(item) {
    return clean((item && (item.product_title || item.title || item.handle)) || 'Товар VSKLO');
  }

  function addonParentTitle(item) {
    return firstProp(item, ['До товару', '_vsklo_parent_title', 'Parent product', 'Товар']) || '';
  }

  function addonServiceTitle(item) {
    return firstProp(item, ['Послуга', '_vsklo_service_title']) || itemTitle(item);
  }

  function itemGroupId(item) {
    return firstProp(item, ['_vsklo_group_id', 'VSKLO_GROUP_ID']);
  }

  function itemVariantId(item) {
    return clean(item && (item.variant_id || item.id));
  }

  function serviceParentVariantId(item) {
    return firstProp(item, ['_vsklo_parent_variant_id', '_linked_variant_id', 'VSKLO_PARENT_VARIANT_ID']);
  }

  function servicePhysicalParent(service, physicalItems) {
    var groupId = itemGroupId(service);
    if (groupId) {
      var groupMatches = physicalItems.filter(function (parent) {
        return itemGroupId(parent) === groupId;
      });
      // A Shopify automatic discount may split ONE parent variant into multiple
      // priced cart lines without changing their shared personalization group.
      // Match the linked parent variant if supplied; reject a truly ambiguous
      // group containing distinct physical variants, never guess by title.
      if (!groupMatches.length) return null;
      var linkedVariant = serviceParentVariantId(service);
      var validMatches = linkedVariant
        ? groupMatches.filter(function (parent) { return itemVariantId(parent) === linkedVariant; })
        : groupMatches;
      if (!validMatches.length) return null;
      var variantIds = Object.create(null);
      validMatches.forEach(function (parent) { variantIds[itemVariantId(parent)] = true; });
      return Object.keys(variantIds).length === 1 ? validMatches[0] : null;
    }

    var parentVariantId = serviceParentVariantId(service);
    if (parentVariantId) {
      var variantMatches = physicalItems.filter(function (parent) {
        return itemVariantId(parent) === clean(parentVariantId);
      });
      // Legacy variant-only links are safe only when the candidate is unique.
      // Two separate lines with the same variant are different parent/add-on pairs.
      return variantMatches.length === 1 ? variantMatches[0] : null;
    }

    var parentTitle = lower(addonParentTitle(service));
    if (parentTitle) {
      var titleMatches = physicalItems.filter(function (parent) {
        return lower(itemTitle(parent)) === parentTitle;
      });
      return titleMatches.length === 1 ? titleMatches[0] : null;
    }

    return null;
  }

  function serviceHasPhysicalParent(service, physicalItems) {
    return !!servicePhysicalParent(service, physicalItems);
  }

  // Quantity validation replaces naive "duplicate" detection. Shopify can
  // legitimately split identical services by automatic price allocations.
  // Aggregate each service variant/type within a logical group, then compare
  // against the associated physical parent quantity.
  function duplicateCanonicalService(items, physicalItems) {
    var quantities = Object.create(null);
    var limits = Object.create(null);
    var firstService = Object.create(null);
    items.forEach(function (item) {
      if (!isServiceItem(item)) return;
      var policy = lower(firstProp(item, ['_vsklo_quantity_policy'])).replace(/[\s-]+/g, '_');
      if (policy !== 'linked_delta' && policy !== 'per_parent_unit') return;
      var groupId = itemGroupId(item);
      var variantId = itemVariantId(item);
      if (!groupId || !variantId) return;
      var parent = servicePhysicalParent(item, physicalItems);
      if (!parent) return; // orphan check has a more useful error
      var physicalVariant = itemVariantId(parent);
      var groupPhysicalQty = physicalItems.reduce(function (sum, candidate) {
        return sum + (
          itemGroupId(candidate) === groupId &&
          itemVariantId(candidate) === physicalVariant
            ? Math.max(0, Number(candidate.quantity) || 0) : 0
        );
      }, 0);
      var key = groupId + '|' + variantId + '|' + normalizedServiceType(item);
      quantities[key] = (quantities[key] || 0) + Math.max(0, Number(item.quantity) || 0);
      limits[key] = groupPhysicalQty;
      if (!firstService[key]) firstService[key] = item;
    });
    var overLimit = Object.keys(quantities).find(function (key) {
      return quantities[key] > limits[key];
    });
    return overLimit ? firstService[overLimit] : null;
  }

  function normalizedServiceType(item) {
    return lower(firstProp(item, ['_vsklo_service_type'])).replace(/[\s-]+/g, '_');
  }

  function duplicateSingletonService(items) {
    var seen = Object.create(null);
    for (var index = 0; index < items.length; index += 1) {
      var item = items[index];
      if (!isServiceItem(item)) continue;

      var type = normalizedServiceType(item);
      if (type === 'gift_package' || type === 'packaging' || type === 'package' || type === 'gift_bag' || type === 'bag') {
        type = 'gift_packaging';
      }
      if (type !== 'gift_packaging') continue;

      var groupId = itemGroupId(item);
      if (!groupId) continue;

      var key = groupId + '|' + type;
      var packagingVariant = itemVariantId(item);
      if (seen[key] && seen[key] !== packagingVariant) return item;
      seen[key] = packagingVariant;
    }
    return null;
  }

  function serviceRequiresText(item) {
    if (!isServiceItem(item)) return false;
    var noText = lower(firstProp(item, ['_vsklo_no_engraving'])) === 'true';
    if (noText) return false;

    var type = lower(firstProp(item, ['_vsklo_service_type'])).replace(/[\s-]+/g, '_');
    if (type === 'gift_packaging' || type === 'gift_package' || type === 'packaging' || type === 'package') return false;
    if (type === 'postcard') return true;
    if (type.indexOf('engraving') === 0 || type.indexOf('_engraving') !== -1) return true;

    // Compatibility for legacy carts created before structured service types.
    var title = lower(addonServiceTitle(item));
    return title.indexOf('грав') !== -1 || title.indexOf('engraving') !== -1 || title.indexOf('нанес') !== -1 ||
      title.indexOf('листів') !== -1 || title.indexOf('postcard') !== -1;
  }

  function serviceHasRequiredText(item) {
    if (!serviceRequiresText(item)) return true;

    var mode = lower(firstProp(item, ['_vsklo_engraving_mode', '_vsklo_personalization_mode']));
    if (mode === 'design') {
      return !!firstProp(item, [
        '_vsklo_design_id',
        '_vsklo_design_code',
        '_vsklo_design_title',
        'Дизайн',
        'Код дизайну',
      ]);
    }
    if (mode === 'upload') {
      return !!firstProp(item, [
        '_vsklo_upload_file',
        '_vsklo_upload_filename',
        'Файл гравіювання',
        'Engraving file',
        'Gravurdatei',
        'Plik do grawerowania',
      ]);
    }

    return !!firstProp(item, [
      '_vsklo_engraving_text',
      '_vsklo_postcard_text',
      'Текст гравіювання',
      'Текст листівки',
      'Текст нанесення',
      'Текст',
    ]);
  }

  function validateCartIntegrity(cart) {
    var items = cartItems(cart);
    if (!items.length) {
      throw new Error(t('cart', 'empty', 'Кошик порожній'));
    }

    var physicalItems = items.filter(function (item) {
      return !isServiceItem(item);
    });

    if (!physicalItems.length) {
      throw new Error(
        t(
          'cart',
          'orphanServicesWarning',
          'У кошику залишились тільки послуги без основного товару. Очистіть кошик і додайте товар заново.',
        ),
      );
    }

    var orphanService = items.find(function (item) {
      return isServiceItem(item) && !serviceHasPhysicalParent(item, physicalItems);
    });

    if (orphanService) {
      throw new Error(
        t(
          'cart',
          'orphanServicesWarning',
          'У кошику є послуга без пов’язаного основного товару. Видаліть її або додайте товар заново перед оформленням.',
        ),
      );
    }

    var duplicateService = duplicateCanonicalService(items, physicalItems);
    if (duplicateService) {
      throw new Error(
        t(
          'cart',
          'excessAddonQuantity',
          'Кількість додаткових послуг перевищує кількість відповідних товарів. Зменште кількість послуг перед оформленням.',
        ),
      );
    }

    var duplicateSingleton = duplicateSingletonService(items);
    if (duplicateSingleton) {
      throw new Error(
        t(
          'cart',
          'duplicateGiftPackaging',
          'Для одного товару вибрано кілька варіантів подарункового пакування. Залиште один варіант перед оформленням.',
        ),
      );
    }

    var serviceWithoutText = items.find(function (item) {
      return isServiceItem(item) && !serviceHasRequiredText(item);
    });

    if (serviceWithoutText) {
      throw new Error(
        t(
          'addons',
          'fillRequiredServiceText',
          'Текст для вибраної послуги не збережений у кошику. Заповніть його та спробуйте оформити замовлення ще раз.',
        ),
      );
    }

    return cart;
  }

  function itemKindForBackup(item) {
    var text = lower([itemTitle(item), addonServiceTitle(item), prop(item, '_vsklo_service_type'), item && item.product_type].join(' '));
    if (text.indexOf('листів') !== -1 || text.indexOf('postcard') !== -1 || text.indexOf('card') !== -1) return 'postcard';
    if (text.indexOf('грав') !== -1 || text.indexOf('engraving') !== -1 || text.indexOf('нанес') !== -1) return 'engraving';
    if (text.indexOf('пакет') !== -1 || text.indexOf('пакув') !== -1 || text.indexOf('pack') !== -1 || text.indexOf('bag') !== -1)
      return 'packaging';
    return 'service';
  }

  function personalizationTextForItem(item) {
    return firstProp(item, [
      'Текст гравіювання',
      'Текст нанесення',
      'Текст листівки',
      'Текст побажання',
      'Текст',
      'Напис',
      'Побажання',
      'Коментар до послуги',
      '_vsklo_engraving_text',
      '_vsklo_postcard_text',
      '_vsklo_text',
      '_vsklo_personalization_text',
      '_vsklo_design_title',
      '_vsklo_design_code',
      '_vsklo_upload_filename',
    ]);
  }

  function buildPersonalizationBackup(cart) {
    var rows = [];
    var note = clean(cart && cart.note);
    if (note) rows.push('💬 Коментар до замовлення: ' + note);

    cartItems(cart).forEach(function (item) {
      if (!isServiceItem(item)) return;
      var kind = itemKindForBackup(item);
      var text = personalizationTextForItem(item);
      var service = addonServiceTitle(item).replace(/\*+$/g, '').trim();
      var parent = addonParentTitle(item);

      if (kind === 'postcard') {
        rows.push('💌 Листівка' + (parent ? ' до «' + parent + '»' : '') + (text ? ': ' + text : ': текст не вказано'));
        return;
      }

      if (kind === 'engraving') {
        rows.push('✍️ ' + service + (parent ? ' до «' + parent + '»' : '') + (text ? ': ' + text : ': текст не вказано'));
      }
    });

    return rows.join('\n').slice(0, 4500);
  }

  function directBackupEndpoint() {
    var script = document.querySelector('[data-vsklo-personalization-backup-url]');
    var enabledAttr = script && script.getAttribute('data-vsklo-personalization-backup-enabled');
    var enabled = String(enabledAttr == null ? 'true' : enabledAttr).toLowerCase() !== 'false';
    if (!enabled) return '';

    var fromAttr = script && script.getAttribute('data-vsklo-personalization-backup-url');
    var endpoint = clean(window.VSKLO_PERSONALIZATION_BACKUP_URL || fromAttr || '');
    if (!endpoint || endpoint.indexOf('PASTE_APPS_SCRIPT_WEB_APP_URL_HERE') !== -1) return '';
    if (!/^https:\/\//i.test(endpoint)) {
      console.warn('VSKLO personalization backup endpoint must use HTTPS.');
      return '';
    }
    return endpoint;
  }

  function visibleCustomerData() {
    function val(selector) {
      var el = document.querySelector(selector);
      return el ? clean(el.value || el.textContent || '') : '';
    }
    return {
      name: val('[name="name"], [name="customer_name"], [data-vsklo-customer-name]'),
      phone: val('[name="phone"], [name="telephone"], [data-vsklo-customer-phone], input[type="tel"]'),
      email: val('[name="email"], [data-vsklo-customer-email], input[type="email"]'),
    };
  }

  function cartSkuList(cart) {
    return cartItems(cart)
      .map(function (item) {
        return clean(item.sku);
      })
      .filter(Boolean);
  }

  function cartVariantIdList(cart) {
    return cartItems(cart)
      .map(function (item) {
        return String(item.variant_id || item.id || '').trim();
      })
      .filter(Boolean);
  }

  function readAttributionForBackup(cart) {
    var stored = {};
    var cartAttributes = cart && cart.attributes && typeof cart.attributes === 'object' ? cart.attributes : {};

    try {
      stored = window.VSKLO_ATTRIBUTION || JSON.parse(window.localStorage.getItem('vsklo_attribution_v2') || '{}') || {};
    } catch (error) {
      stored = {};
    }

    var current = {};
    if (typeof window.VSKLO_GET_ATTRIBUTION_FOR_CRM === 'function') {
      try {
        current = window.VSKLO_GET_ATTRIBUTION_FOR_CRM() || {};
      } catch (error) {
        current = {};
      }
    }

    return {
      source: 'vsklo.com',
      version: 'v130-theme-utm',
      pageUrl: clean(window.location.href || ''),
      referrer: clean(document.referrer || ''),
      cartAttributes: cartAttributes,
      stored: stored,
      current: current,
    };
  }

  function backupItems(cart) {
    return cartItems(cart).map(function (item) {
      return {
        title: itemTitle(item),
        productTitle: clean(item.product_title || ''),
        variantId: String(item.variant_id || item.id || ''),
        productId: String(item.product_id || ''),
        sku: clean(item.sku || ''),
        quantity: Number(item.quantity || 1),
        price: Number(item.price || 0),
        linePrice: Number(item.line_price || 0),
        url: clean(item.url || ''),
        role: isServiceItem(item) ? 'addon' : 'main',
        kind: isServiceItem(item) ? itemKindForBackup(item) : 'main',
        parentTitle: addonParentTitle(item),
        serviceTitle: isServiceItem(item) ? addonServiceTitle(item) : '',
        personalizationText: personalizationTextForItem(item),
        properties: propsOf(item),
      };
    });
  }

  function buildDirectBackupPayload(cart) {
    var token = getBestCartToken(cart);
    var createdAt = new Date().toISOString();
    var visibleNote = visibleCartNoteValue();
    var note = clean(visibleNote !== null ? visibleNote : ((cart && cart.note) || ''));
    var customer = visibleCustomerData();
    var items = backupItems(cart);
    var personalization = buildPersonalizationBackup(cart);
    var total = Number((cart && cart.total_price) || 0);
    var itemCount = Number((cart && cart.item_count) || items.length || 0);
    var backupId = [token || 'no-token', total, itemCount, Math.floor(Date.now() / 60000)].join('-');

    return {
      type: 'vsklo_personalization_backup',
      version: '2026-06-10-v130-stable-checkout-direct-backup',
      backupId: backupId,
      createdAt: createdAt,
      shop: clean((window.Shopify && window.Shopify.shop) || ''),
      storefront: clean(window.location.host || ''),
      pageUrl: clean(window.location.href || ''),
      cartToken: token,
      currency: clean((cart && cart.currency) || (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || ''),
      totalPrice: total,
      totalFormatted: total ? String(Math.round(total) / 100) : '',
      itemCount: itemCount,
      note: note,
      customer: customer,
      attribution: readAttributionForBackup(cart),
      personalization: personalization,
      skus: cartSkuList(cart),
      variantIds: cartVariantIdList(cart),
      items: items,
    };
  }

  function sendDirectPersonalizationBackupBeforeCheckout(cart) {
    var endpoint = directBackupEndpoint();
    if (!endpoint || !cart || !Array.isArray(cart.items)) return false;

    var payload = buildDirectBackupPayload(cart);
    if (!payload.personalization && !payload.note) return false;

    var body = JSON.stringify(payload);

    try {
      try {
        window.__VSKLO_LAST_PERSONALIZATION_BACKUP__ = payload;
      } catch (ignoreStore) {}

      var beaconSent = false;
      if (navigator.sendBeacon) {
        try {
          var blob = new Blob([body], { type: 'text/plain;charset=UTF-8' });
          beaconSent = navigator.sendBeacon(endpoint, blob);
        } catch (ignoreBeacon) {}
      }

      if (!beaconSent) {
        fetch(endpoint, {
          method: 'POST',
          mode: 'no-cors',
          credentials: 'omit',
          cache: 'no-store',
          keepalive: true,
          headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
          body: body,
        }).catch(function (error) {
          console.warn('VSKLO direct personalization backup background fetch skipped:', error);
        });
      }

      return true;
    } catch (error) {
      console.warn('VSKLO direct personalization backup skipped:', error);
      return false;
    }
  }

  function isUsableCart(cart) {
    return !!(cart && Array.isArray(cart.items) && cart.items.length > 0);
  }

  function rememberCart(cart) {
    if (!isUsableCart(cart)) return cart;
    cachedCart = cart;
    cachedCartAt = Date.now();
    if (cart.token) window.__VSKLO_CART_TOKEN__ = cart.token;
    return cart;
  }

  function getFreshCachedCart() {
    if (!isUsableCart(cachedCart)) return null;
    if (Date.now() - cachedCartAt > CART_CACHE_TTL_MS) return null;
    return cachedCart;
  }

  async function getCartForCheckout(waitMs) {
    var cached = getFreshCachedCart();
    if (cached) return cached;

    return withTimeout(
      fetchCartFresh()
        .then(rememberCart)
        .catch(function (error) {
          console.warn('VSKLO fresh cart fetch skipped before checkout:', error);
          return null;
        }),
      waitMs,
      null,
    );
  }

  async function prepareCartForChekly() {
    var hadPendingChanges = await runBeforeCheckoutHooks();
    await finishOrphanCleanupBeforeCheckout();
    await syncAttributionBeforeCheckout();

    // withTimeout() does not cancel an in-flight attribution mutation. Wait for
    // the shared writer queue again before reading the cart token/state and
    // redirecting to Chekly, so checkout never races a late /cart/update.js.
    await waitForCartMutationQueue();

    cachedCartAt = 0;
    var waitMs = hadPendingChanges ? CART_PENDING_WAIT_MS : CART_FAST_WAIT_MS;

    var cart = await getCartForCheckout(waitMs);

    if (!cart) {
      throw new Error(t('cart', 'checkoutRedirectError', 'Оформлення тимчасово недоступне. Оновіть сторінку і спробуйте ще раз.'));
    }

    validateCartIntegrity(cart);
    sendDirectPersonalizationBackupBeforeCheckout(cart);

    if (cart.token) window.__VSKLO_CART_TOKEN__ = cart.token;
    return cart;
  }

  function getCookie(name) {
    var pattern = new RegExp('(?:^|; )' + name.replace(/([.$?*|{}()\[\]\\\/+^])/g, '\\$1') + '=([^;]*)');
    var match = document.cookie.match(pattern);
    return match ? decodeURIComponent(match[1]) : '';
  }

  function readVskloStoredAttribution() {
    try {
      var data = window.VSKLO_ATTRIBUTION || JSON.parse(window.localStorage.getItem('vsklo_attribution_v2') || '{}');
      return data || {};
    } catch (error) {
      return {};
    }
  }

  function appendTrackingParams(params) {
    try {
      var query = new URLSearchParams(window.location.search);
      var saved = {};
      var stored = readVskloStoredAttribution();
      var storedLast = stored.last || {};
      var storedFirst = stored.first || {};
      try {
        saved = JSON.parse(getCookie('chekly_tracking') || '{}');
      } catch (error) {}
      [
        'utm_campaign',
        'utm_medium',
        'utm_source',
        'utm_content',
        'utm_term',
        'utm_id',
        'fbclid',
        'ttclid',
        'epik',
        'ScCid',
        'gclid',
        'gbraid',
        'wbraid',
        'msclkid',
      ].forEach(function (key) {
        var value = query.get(key) || saved[key] || storedLast[key] || storedFirst[key] || '';
        if (value) params.set(key === 'ScCid' ? 'sccid' : key, value);
      });
      ['_fbc', '_fbp', '_ttp'].forEach(function (key) {
        var value = getCookie(key);
        if (value) params.set(key === '_ttp' ? 'ttp' : key, value);
      });

      var customerComment = checkoutCustomerComment();
      if (customerComment) params.set('utm_comment', customerComment);
    } catch (error) {}
  }

  function getBestCartToken(cart) {
    return clean(cart && cart.token) || clean(window.__VSKLO_CART_TOKEN__) || getCookie('cart') || '';
  }

  function buildCheklyCheckoutUrl(cart) {
    var script = document.querySelector('[data-chekly-url]');
    var appBaseUrl =
      (script && (script.getAttribute('data-checkout-domain') || script.getAttribute('data-chekly-url'))) || 'https://chekly-app.com';
    var cartToken = getBestCartToken(cart);
    var storefrontHost = window.location.host || window.location.origin.replace(/^https?:\/\//, '');
    var shopifyDomain = clean((window.Shopify && window.Shopify.shop) || '');
    var merchantStoreName = shopifyDomain || storefrontHost;
    if (!appBaseUrl || !cartToken || !merchantStoreName) return null;

    var checkoutUrl = new URL('/checkout/', appBaseUrl);
    var rootRoute = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    var currencyRate = (window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) || 1;
    var theme = (script && script.getAttribute('data-chekly-theme')) || 'chekly';

    checkoutUrl.searchParams.set('storeName', merchantStoreName);
    checkoutUrl.searchParams.set('cartToken', cartToken);
    checkoutUrl.searchParams.set('utm_cart_token', cartToken);
    checkoutUrl.searchParams.set('curRate', String(currencyRate));
    if (theme) checkoutUrl.searchParams.set('theme', theme);
    if (shopifyDomain) checkoutUrl.searchParams.set('shopifyDomain', shopifyDomain);
    if (storefrontHost) checkoutUrl.searchParams.set('storefrontDomain', storefrontHost);
    if (rootRoute && rootRoute.length > 2) checkoutUrl.searchParams.set('subFolder', rootRoute.replace(/\//g, ''));
    appendTrackingParams(checkoutUrl.searchParams);
    return checkoutUrl;
  }

  function directCheklyRedirect(cart) {
    try {
      var checkoutUrl = buildCheklyCheckoutUrl(cart);
      if (!checkoutUrl) return false;
      window.location.href = checkoutUrl.toString();
      return true;
    } catch (error) {
      console.warn('VSKLO canonical Chekly checkout URL build failed:', error);
      return false;
    }
  }

  function isVisibleField(field) {
    if (!field) return false;
    return !(field.offsetParent === null && field.getClientRects().length === 0);
  }

  function markInvalid(field) {
    field.classList.add('is-error');
    field.setAttribute('aria-invalid', 'true');
    var row = field.closest('[data-vsklo-addon-row], .vsklo-cart-addon-card, .vsklo-cart-popup-addon, .product-addons-popup__addon');
    if (row) row.classList.add('vsklo-addon-field-error');
  }

  function validateRequiredServiceText() {
    var invalid = null;
    var fields = document.querySelectorAll(
      '[data-vsklo-engraving-row] input[data-vsklo-property-name], [data-vsklo-addon-text][required], [data-vsklo-same-text-field], [data-vsklo-text-part]',
    );
    fields.forEach(function (field) {
      if (invalid) return;
      if (!isVisibleField(field)) return;
      var row = field.closest('[data-vsklo-addon-row]');
      if (row && (row.getAttribute('data-no-engraving') === 'true' || row.dataset.noEngraving === 'true')) return;
      if (!clean(field.value)) invalid = field;
    });
    if (!invalid) return true;
    markInvalid(invalid);
    showMessage(t('addons', 'fillRequiredServiceText', 'Заповніть текстове поле для вибраної послуги'));
    try {
      invalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } catch (error) {}
    window.setTimeout(function () {
      try {
        invalid.focus();
      } catch (error) {}
    }, 120);
    return false;
  }

  function checkoutElement(target) {
    if (!target || !target.closest) return null;
    if (target.closest('[data-vsklo-minicart-go-cart="true"]')) return null;
    var link = target.closest('a[href]');
    if (link) {
      var href = clean(link.getAttribute('href'));
      if (href && href.indexOf('/cart') !== -1 && href.indexOf('/checkout') === -1) return null;
    }
    return target.closest(
      '[data-vsklo-checkout], [name="checkout"], #checkout, .btn-checkout, .cart__checkout-button, .vsklo-checkout-button, a[href*="/checkout"]',
    );
  }

  function isCheckoutSubmit(event) {
    var form = event.target && event.target.closest ? event.target.closest('form') : null;
    if (!form) return false;
    var action = clean(form.getAttribute('action'));
    if (action.indexOf('/cart') === -1 && form.id !== 'cart' && form.id !== 'minicart') return false;
    var submitter = event.submitter || document.activeElement;
    if (submitter && submitter.closest && submitter.closest('[data-vsklo-minicart-go-cart="true"]')) return false;
    if (
      submitter &&
      submitter.matches &&
      submitter.matches('[data-vsklo-checkout], [name="checkout"], .btn-checkout, .cart__checkout-button, .vsklo-checkout-button')
    )
      return true;
    return !!form.querySelector('[data-vsklo-checkout], [name="checkout"], .btn-checkout, .cart__checkout-button, .vsklo-checkout-button');
  }

  function suppress(event) {
    if (!event) return;
    event.preventDefault();
    event.stopPropagation();
    if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();
  }

  async function handleCheckout(event, element) {
    suppress(event);
    if (!element || checkoutInProgress || window.__VSKLO_CHECKOUT_REDIRECTING__) return;

    if (!isCartPage()) {
      checkoutInProgress = true;
      window.__VSKLO_CHECKOUT_REDIRECTING__ = true;
      element.setAttribute('aria-busy', 'true');
      if ('disabled' in element) element.disabled = true;
      element.classList.add('vsklo-checkout-is-processing');

      try {
        if (typeof window.VSKLOMinicartFlushPending === 'function') {
          await withTimeoutError(
            Promise.resolve(window.VSKLOMinicartFlushPending()),
            5000,
            t('cart', 'checkoutSyncError', 'Не вдалося зберегти зміни у кошику. Спробуйте ще раз.'),
          );
        }
        redirectToCartPage();
      } catch (error) {
        checkoutInProgress = false;
        window.__VSKLO_CHECKOUT_REDIRECTING__ = false;
        element.removeAttribute('aria-busy');
        if ('disabled' in element) element.disabled = false;
        element.classList.remove('vsklo-checkout-is-processing');
        console.error('VSKLO minicart sync failed before cart redirect', error);
        showMessage(
          error && error.message
            ? error.message
            : t('cart', 'checkoutSyncError', 'Не вдалося зберегти зміни у кошику. Спробуйте ще раз.'),
        );
      }
      return;
    }

    if (!validateRequiredServiceText()) return;

    checkoutInProgress = true;
    window.__VSKLO_CHECKOUT_REDIRECTING__ = true;
    element.setAttribute('aria-busy', 'true');
    if ('disabled' in element) element.disabled = true;
    element.classList.add('vsklo-checkout-is-processing');

    try {
      if (!useCheklyCheckout()) {
        await prepareCartForNativeCheckout();
        redirectToNativeCheckout();
        return;
      }

      var preparedCart = await prepareCartForChekly();
      if (directCheklyRedirect(preparedCart)) return;
      if (window.VskloCheklyCheckout && typeof window.VskloCheklyCheckout.processCheckout === 'function') {
        if (window.VskloCheklyCheckout.processCheckout({ source: 'vsklo-checkout-guard-country-context' })) return;
      }
      throw new Error(t('cart', 'checkoutRedirectError', 'Оформлення тимчасово недоступне. Оновіть сторінку і спробуйте ще раз.'));
    } catch (error) {
      checkoutInProgress = false;
      window.__VSKLO_CHECKOUT_REDIRECTING__ = false;
      element.removeAttribute('aria-busy');
      if ('disabled' in element) element.disabled = false;
      element.classList.remove('vsklo-checkout-is-processing');
      console.error('VSKLO checkout guard failed', error);
      showMessage(
        error && error.message
          ? error.message
          : t('cart', 'checkoutRedirectError', 'Оформлення тимчасово недоступне. Оновіть сторінку і спробуйте ще раз.'),
      );
    }
  }

  window.addEventListener(
    'click',
    function (event) {
      var element = checkoutElement(event.target);
      if (!element) return;
      handleCheckout(event, element);
    },
    true,
  );

  window.addEventListener(
    'submit',
    function (event) {
      if (!isCheckoutSubmit(event)) return;
      var element =
        event.submitter ||
        (event.target &&
          event.target.querySelector(
            '[data-vsklo-checkout], [name="checkout"], .btn-checkout, .cart__checkout-button, .vsklo-checkout-button',
          ));
      handleCheckout(event, element);
    },
    true,
  );

  document.addEventListener(
    'input',
    function (event) {
      if (
        event.target.closest(
          '[data-vsklo-property-name], .vsklo-cart-edit-input, [data-vsklo-same-text-field], [data-vsklo-text-part], [data-vsklo-addon-text]',
        )
      ) {
        event.target.classList.remove('is-error');
        var row = event.target.closest('[data-vsklo-addon-row], .vsklo-cart-addon-card');
        if (row) row.classList.remove('vsklo-addon-field-error');
      }
    },
    true,
  );

  function resetCheckoutState() {
    checkoutInProgress = false;
    window.__VSKLO_CHECKOUT_REDIRECTING__ = false;
    document.querySelectorAll('[aria-busy="true"]').forEach(function (element) {
      if (checkoutElement(element)) element.removeAttribute('aria-busy');
    });
    if (window.VskloCheklyCheckout && typeof window.VskloCheklyCheckout.reset === 'function') window.VskloCheklyCheckout.reset();
  }

  var cartPrimeTimer = null;
  var cartPrimeIdle = null;

  function primeCartCache(delay) {
    if (cartPrimeTimer) window.clearTimeout(cartPrimeTimer);
    if (cartPrimeIdle && window.cancelIdleCallback) window.cancelIdleCallback(cartPrimeIdle);
    cartPrimeTimer = window.setTimeout(function () {
      cartPrimeTimer = null;
      if (getFreshCachedCart()) return;

      var run = function () {
        cartPrimeIdle = null;
        if (getFreshCachedCart()) return;
        fetchCartFresh().catch(function (error) {
          if (window.VSKLO_DEBUG) console.warn('VSKLO cart prefetch skipped:', error);
        });
      };

      if (window.requestIdleCallback) {
        cartPrimeIdle = window.requestIdleCallback(run, { timeout: 900 });
      } else {
        run();
      }
    }, typeof delay === 'number' ? delay : 240);
  }

  window.addEventListener('pageshow', function () {
    resetCheckoutState();
    primeCartCache(40);
  });

  document.addEventListener('visibilitychange', function () {
    if (!document.hidden) {
      resetCheckoutState();
      primeCartCache(80);
    }
  });

  document.addEventListener('vsklo:cart:changed', function (event) {
    var cart = event && event.detail && event.detail.cart;
    if (cart && Array.isArray(cart.items)) rememberCart(cart);
    else if (cart && cart.token) window.__VSKLO_CART_TOKEN__ = cart.token;
    else primeCartCache(120);
  });

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      function () {
        primeCartCache(240);
      },
      { once: true },
    );
  } else {
    primeCartCache(240);
  }

  window.VSKLOBuildCheklyCheckoutUrl = buildCheklyCheckoutUrl;
  window.VSKLOPrepareCartForChekly = prepareCartForChekly;

  window.VSKLOClearVisibleCustomerCommentFields = function () {
    document.querySelectorAll('[data-vsklo-customer-comment], #Cart-note, textarea[name="note"]').forEach(function (field) {
      field.value = '';
    });
  };

  window.VSKLOClearCartMeta = function () {
    var task = function () {
      return postJson(route('/cart/update.js'), { note: '', attributes: {} });
    };
    var queue = window.VSKLOCartMutationQueue;
    var promise = queue && typeof queue.enqueue === 'function'
      ? queue.enqueue('clear-cart-meta', task)
      : task();
    // Preserve the queue rejection so callers never continue with stale cart metadata.
    return promise;
  };
})();